import { useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { PolicyCancelationEstimatePostRequestBody } from '@lio/api-types';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { useGetPolicyCancelationEstimate } from './policies';

/**
 * Debounces a cancelation return-premium estimate request as its inputs
 * change, and immediately clears any previously-loaded estimate on every
 * input change (not just once the debounce settles) so a stale premium for
 * the prior selection is never shown alongside new, unconfirmed inputs --
 * only the fetch itself is delayed.
 *
 * `params` is `null` whenever the caller doesn't yet have (or shouldn't
 * request) an estimate -- e.g. required fields aren't filled in yet, or the
 * dialog is read-only for an already-processed transaction.
 */
export const useCancelationReturnPremiumEstimate = (
  policyId: string,
  params: PolicyCancelationEstimatePostRequestBody | null,
) => {
  const { getCancelationEstimate, isPending, isError, data: estimate, reset } = useGetPolicyCancelationEstimate();

  const debouncedFetch = useDebouncedCallback((requestParams: PolicyCancelationEstimatePostRequestBody) => {
    getCancelationEstimate({ policyId, ...requestParams }).catch(() => {
      // Error state is surfaced via isError; nothing further to do here.
    });
  }, DEBOUNCE_DELAYS.FORM_TYPING);

  useEffect(() => {
    reset();
    if (!params) {
      debouncedFetch.cancel();
      return;
    }
    debouncedFetch(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.method, params?.source, params?.cancellationEffectiveOn]);

  return { estimate, isPending, isError };
};
